import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CapabilitiesResponseDataSchema,
  CapabilityCampaignAssignation,
  CapabilityIdentifier,
  CapabilityResponseDataSchema,
  ReactToMessageCapabilityConfigSchema,
} from "schema"
import { toast } from "sonner"
import z from "zod"
import { useInstallCapabilityMutation } from "../../hooks/useInstallCapabilityMutation"
import { useUpdateCapabilityConfigMutation } from "../../hooks/useUpdateCapabilityConfigMutation"
import { ReactToMessageConfig, ReactToMessageFields } from "./ReactToMessageFields"

type Props = {
  capability: z.infer<typeof CapabilitiesResponseDataSchema>[number]
  mode?: "install" | "update"
  initialConfig?: ReactToMessageConfig
  onClose: () => void
  onPendingChange?: (isPending: boolean) => void
  onConfigure?: (capability: z.infer<typeof CapabilityResponseDataSchema>) => void
  onSubmitHandlerRef?: React.MutableRefObject<
    | ((
        campaignAssignation: CapabilityCampaignAssignation,
        campaignIds: string[],
        campaignFlow?: "Inbound" | "Outbound",
      ) => Promise<void>)
    | null
  >
}

export function ReactToMessageForm({
  capability,
  mode = "install",
  initialConfig,
  onClose,
  onPendingChange,
  onConfigure,
  onSubmitHandlerRef,
}: Props) {
  const { t } = useTranslation()
  const parsedInitialConfig = ReactToMessageCapabilityConfigSchema.parse(initialConfig ?? {})
  const [config, setConfig] = useState<ReactToMessageConfig>({
    emojis: parsedInitialConfig.emojis,
    definition: parsedInitialConfig.definition,
  })

  const { mutateAsync: installCapability, isPending: isInstallPending } =
    useInstallCapabilityMutation()
  const { mutateAsync: updateCapabilityConfig, isPending: isUpdatePending } =
    useUpdateCapabilityConfigMutation()

  const isPending = mode === "install" ? isInstallPending : isUpdatePending

  useEffect(() => {
    onPendingChange?.(isPending)
  }, [isPending, onPendingChange])

  const handleSubmit = useCallback(
    async (
      campaignAssignation: CapabilityCampaignAssignation,
      campaignIds: string[],
      campaignFlow?: "Inbound" | "Outbound",
    ) => {
      if (isPending) return

      let installedCapability: z.infer<typeof CapabilityResponseDataSchema> | undefined

      if (mode === "install") {
        installedCapability = await installCapability({
          identifier: CapabilityIdentifier.ReactToMessage,
          config,
          campaignAssignation,
          campaignIds,
          campaignFlow,
        })

        toast.success(
          t("capabilities.list.install.success", {
            capability: t("capabilities.catalog.ReactToMessage.name"),
          }),
        )
      } else {
        await updateCapabilityConfig({ id: capability.id, config })
      }

      if (onConfigure && installedCapability) {
        onConfigure(installedCapability)
      } else {
        onClose()
      }
    },
    [
      isPending,
      mode,
      config,
      capability.id,
      installCapability,
      updateCapabilityConfig,
      onConfigure,
      onClose,
      t,
    ],
  )

  if (onSubmitHandlerRef) {
    onSubmitHandlerRef.current = handleSubmit
  }

  return <ReactToMessageFields value={config} onChange={setConfig} />
}
